import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Bot, Brain, ChartNoAxesCombined, ChevronRight, FileText, Globe2, LockKeyhole, LogIn, RefreshCw, Settings2, ShieldCheck, TriangleAlert, Wallet } from 'lucide-react';
import { formatPrice, normalizeSymbol, toFinitePrice } from '@/lib/forexUtils';
import { marketTimeLabel } from '@/lib/marketEvidence';
import { forexApi, type MarketTickerDTO } from '@/services/forexApi';
import './GuestHome.css';

const features = [
  { Icon: Bot, title: 'AI 智能体', description: '查看平台智能体与策略说明', path: '/agent', tone: 'purple' },
  { Icon: Settings2, title: '跟单配置', description: '绑定交易账户，管理跟随规则', path: '/copy-config', tone: 'violet' },
  { Icon: Wallet, title: '跟单表现', description: '查看个人跟随与执行记录', path: '/assets', tone: 'green' },
  { Icon: FileText, title: '决策日志', description: '追踪 AI 决策与执行过程', path: '/ai-decisions', tone: 'amber' },
];

const steps = [
  { title: '浏览智能体', description: '了解平台智能体与策略说明' },
  { title: '绑定交易账户', description: '登录后绑定支持的交易账户' },
  { title: '配置跟随', description: '设置跟随规则，查看执行记录' },
];

export default function GuestHome({ onLogin }: { onLogin: () => void }) {
  const [tickers, setTickers] = useState<MarketTickerDTO[]>([]);
  const [state, setState] = useState<'loading' | 'ready' | 'error'>('loading');
  const [retry, setRetry] = useState(0);

  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;
    async function refresh() {
      try {
        const result = await forexApi.listMarketTickers();
        if (!cancelled) {
          setTickers(result.slice(0, 5));
          setState('ready');
        }
      } catch {
        if (!cancelled) {
          setTickers([]);
          setState('error');
        }
      } finally {
        if (!cancelled) timer = setTimeout(refresh, 8000);
      }
    }
    void refresh();
    return () => { cancelled = true; clearTimeout(timer); };
  }, [retry]);

  return <div className="guest-home">
    <header className="guest-header">
      <Link to="/" className="guest-brand" aria-label="ForexMind 首页">
        <span className="guest-brand-mark"><Brain size={25} aria-hidden="true" /></span>
        <span><strong>ForexMind</strong><span className="guest-brand-caption">AI 智能跟单平台</span></span>
      </Link>
      <nav className="guest-desktop-nav" aria-label="主导航">
        <Link to="/" aria-current="page">首页</Link>
        <Link to="/agent">跟单</Link>
        <Link to="/assets">收益</Link>
        <Link to="/profile">我的</Link>
      </nav>
      <button type="button" className="guest-login guest-header-login" onClick={onLogin}><LogIn size={17} aria-hidden="true" />登录 / 注册</button>
    </header>

    <main className="guest-main">
      <section className="guest-hero" aria-labelledby="guest-title">
        <div className="guest-hero-copy">
          <p className="guest-eyebrow">让策略与执行清晰可见</p>
          <h1 id="guest-title"><span>AI</span> 智能跟单，<br />从了解策略开始</h1>
          <p className="guest-intro">浏览平台智能体，登录后绑定交易账户并配置跟随。</p>
          <button type="button" className="guest-login guest-hero-login" onClick={onLogin}><LogIn size={20} aria-hidden="true" />登录 / 注册<ArrowRight size={20} aria-hidden="true" /></button>
          <p className="guest-login-note"><LockKeyhole size={14} aria-hidden="true" />登录后管理账户、跟随与执行记录</p>
        </div>
        <div className="guest-visual" aria-hidden="true">
          <div className="guest-orbit guest-orbit-one" /><div className="guest-orbit guest-orbit-two" />
          <span className="guest-orbit-dot guest-dot-one" /><span className="guest-orbit-dot guest-dot-two" /><span className="guest-orbit-dot guest-dot-three" />
          <div className="guest-visual-core"><Brain size={62} strokeWidth={1.5} /></div>
          <div className="guest-visual-label guest-label-strategy"><ChartNoAxesCombined /><span><strong>智能策略</strong><small>了解策略逻辑</small></span></div>
          <div className="guest-visual-label guest-label-risk"><ShieldCheck /><span><strong>风险管理</strong><small>查看跟随规则</small></span></div>
          <div className="guest-visual-label guest-label-follow"><Bot /><span><strong>自动跟随</strong><small>跟踪执行记录</small></span></div>
          <div className="guest-visual-label guest-label-audit"><FileText /><span><strong>决策可查</strong><small>追踪决策过程</small></span></div>
        </div>
      </section>

      <ol className="guest-steps" aria-label="开始使用的三个步骤">
        {steps.map((step, index) => <li key={step.title}>
          <span className="guest-step-number">0{index + 1}</span>
          <div><h2>{step.title}</h2><p>{step.description}</p></div>
          {index < steps.length - 1 && <ChevronRight className="guest-step-chevron" size={19} aria-hidden="true" />}
        </li>)}
      </ol>

      <section className="guest-section" aria-labelledby="guest-market-title">
        <div className="guest-section-heading">
          <h2 id="guest-market-title"><Globe2 size={20} aria-hidden="true" />市场预览 <span>公开买价</span></h2>
          <span className="guest-section-note">报价时间为本地时间 · 每 8 秒刷新</span>
        </div>
        <div role="status" aria-live="polite">
          {state === 'loading' && <p className="guest-market-message">正在加载公开行情…</p>}
          {state === 'error' && <div className="guest-market-message guest-market-error"><span>行情暂不可用，正在自动重试</span><button type="button" onClick={() => { setState('loading'); setRetry(value => value + 1); }}><RefreshCw size={15} aria-hidden="true" />重试</button></div>}
          {state === 'ready' && tickers.length === 0 && <p className="guest-market-message">暂无公开行情</p>}
        </div>
        {tickers.length > 0 && <ul className="guest-market-list" aria-label="公开行情报价" tabIndex={0}>
          {tickers.map(ticker => {
            const pair = normalizeSymbol(ticker.symbol);
            const price = toFinitePrice(ticker.bid);
            const fresh = ticker.freshness_status === 'fresh' && price != null && price > 0;
            const status = fresh ? '报价已更新' : ticker.freshness_status === 'stale' ? '报价延迟' : '报价待更新';
            return <li key={ticker.symbol} className="guest-quote">
              <p className="guest-quote-pair">{pair}</p>
              <p className="guest-quote-price">{fresh ? formatPrice(pair, price, ticker.digits ?? undefined) : '—'}</p>
              <div className="guest-quote-meta"><span className={fresh ? 'guest-quote-status' : ''}>{status}</span><time>{marketTimeLabel(ticker.quote_time)}</time></div>
            </li>;
          })}
        </ul>}
      </section>

      <section className="guest-section" aria-labelledby="guest-features-title">
        <div className="guest-section-heading"><h2 id="guest-features-title"><Bot size={20} aria-hidden="true" />登录后，开启完整跟随体验</h2><span className="guest-section-note"><LockKeyhole size={14} aria-hidden="true" />登录后使用</span></div>
        <div className="guest-feature-grid">
          {features.map(({ Icon, title, description, path, tone }) => <Link key={path} to={`/auth?redirect=${encodeURIComponent(path)}`} className="guest-feature">
            <div className="guest-feature-content"><span className={`guest-feature-icon guest-tone-${tone}`}><Icon size={25} aria-hidden="true" /></span><div><h3>{title}</h3><p>{description}</p></div></div>
            <span className="guest-feature-footer"><LockKeyhole size={13} aria-hidden="true" />登录后使用<ArrowRight size={15} aria-hidden="true" /></span>
          </Link>)}
        </div>
      </section>

      <footer className="guest-footer"><TriangleAlert size={15} aria-hidden="true" /><p>风险提示：外汇交易存在风险，历史表现不代表未来收益。</p></footer>
    </main>
  </div>;
}
